/* External */
import {
  useMemo,
  memo,
} from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import isEmpty from 'lodash-es/isEmpty';

/* Ott-common */
import { formatDate, isTodayOrTomorrowDay } from '@dtvgo/date-utils';

/* Assets */
import defaultImageMiniEpgDetailCard from '~/assets/theme/images/defaultImageMiniEpgDetailCard.png';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Components */
import LiveProgressBar from '../../../LiveProgressBar';
import LiveInteractiveTag from '../../../LiveInteractiveTag';

/* Hooks */
import useLiveContentTexts from '../../../../hooks/useLiveContentTexts';
import useChannel from '../../../../hooks/useChannel';

/* Utils */
import { DOT_SEPARATOR, ImageRatio, TIME_FORMAT_FOR_SCHEDULES } from '../../../../utils/constants';
import { getImage } from '../../../../utils/imageUtils';
import {
  useFullEpisodeName,
  getRating,
  getSchedule,
  useTitle,
  isPlayingNow,
  joinArrayWithSeparator,
  joinArrayWithSpace,
  isInteractivityAllowed,
} from '../../../../utils/metaDataUtils';

/* Providers */
import { useCurrentTime } from '../../../../providers/CurrentTimeProvider';
import { useUserAccount } from '../../../../providers/UserProvider';

/* Styles */
import './MiniEpgDetailCard.scss';
import useContent from '../../../../hooks/useContent';

const IMG_WIDTH = 576;
const IMG_DIMENSION = ImageRatio.SIXTEEN_BY_NINE;

function MiniEpgDetailCard({ focusedSchedule }) {
  const {
    blackout,
    endTime,
    startTime,
    channelId,
    content: contentId,
  } = focusedSchedule || {};
  const defaultImageMiniEpgDetailCardFlag = getJsonImage('defaultImageMiniEpgDetailCard') || defaultImageMiniEpgDetailCard;

  const content = useContent(contentId);
  const { title, images } = content || {};

  const liveContentTimeLabels = useLiveContentTexts();

  const currentTime = useCurrentTime();

  const { timeZone } = useIntl();

  const isPlayingNowFlag = useMemo(
    () => isPlayingNow(focusedSchedule, currentTime),
    [currentTime, focusedSchedule],
  );

  const contentImage = useMemo(() => {
    const imageSource = getImage(
      images,
      IMG_WIDTH,
      IMG_DIMENSION,
    );

    return <img src={(imageSource) || defaultImageMiniEpgDetailCardFlag} alt={title} />;
  }, [images, title, defaultImageMiniEpgDetailCardFlag]);

  const schedule = useMemo(
    () => getSchedule(focusedSchedule, timeZone),
    [focusedSchedule, timeZone],
  );

  const { title: channelName } = useChannel(channelId) || {};

  const { uid: userId, iso2Code } = useUserAccount() || {};
  const isInteractive = isInteractivityAllowed(focusedSchedule, iso2Code, userId);

  const startDateFormatted = useMemo(() => {
    const day = isTodayOrTomorrowDay(startTime, liveContentTimeLabels);

    return startTime ? `${day} ${formatDate(startTime, TIME_FORMAT_FOR_SCHEDULES, timeZone)}` : '';
  }, [liveContentTimeLabels, startTime, timeZone]);

  const contentTitle = useTitle(content, focusedSchedule);
  const fullEpisodeName = useFullEpisodeName(content);

  const isThereTitleOrSchedules = useMemo(
    () => !!(contentTitle && startTime && endTime) && focusedSchedule && !isEmpty(focusedSchedule),
    [contentTitle, endTime, focusedSchedule, startTime],
  );

  const metadata = useMemo(() => {
    if (isThereTitleOrSchedules) {
      return (
        <div className="dtv-html5-c-mini-epg-detail-card__preview-schedule">
          <h4 className={`dtv-html5-c-mini-epg-detail-card__title ${
            blackout
              ? 'dtv-html5-c-mini-epg__detail-card-metadata__title-inactive'
              : ''}`}
          >
            {isInteractive && (
              <div className="dtv-html5-c-mini-epg-detail-card__interactive-tag">
                <LiveInteractiveTag />
              </div>
            )}
            {contentTitle}
          </h4>

          {!!fullEpisodeName
            && (
              <p className={`dtv-html5-c-mini-epg-detail-card__episode ${
                blackout
                  ? 'dtv-html5-c-mini-epg-detail-card__episode--inactive'
                  : ''}`}
              >
                {fullEpisodeName}
              </p>
            )}
          {!!schedule && !!startDateFormatted && (
          <p className="dtv-html5-c-mini-epg-detail-card__content-information">
            {
              joinArrayWithSpace(
                [
                  getRating(content),
                  joinArrayWithSeparator([
                    isPlayingNowFlag ? schedule : startDateFormatted,
                    channelName,
                  ], DOT_SEPARATOR),
                ],
              )
            }
          </p>
          )}
        </div>
      );
    }
    return (
      <span className="dtv-html5-c-mini-epg-detail-card__missing-metadata">
        <FormattedMessage
          id="schedule.missing.metadata"
          defaultMessage="No hay información de programación disponible"
        />
      </span>
    );
  }, [
    isThereTitleOrSchedules,
    blackout,
    contentTitle,
    content,
    fullEpisodeName,
    isPlayingNowFlag,
    schedule,
    startDateFormatted,
    channelName,
    isInteractive,
  ]);

  const nowPlayingProgressInfo = useMemo(
    () => isPlayingNowFlag && (
      <>
        {metadata}
        <LiveProgressBar
          className="dtv-html5-c-mini-epg-detail-card__preview-progress-bar"
          startDate={startTime}
          endDate={endTime}
        />
      </>
    ),
    [endTime, isPlayingNowFlag, startTime, metadata],
  );

  const imageSchedule = useMemo(() => (
    isThereTitleOrSchedules ? contentImage : (
      <span className="dtv-html5-c-mini-epg-detail-card__blackout-text">
        <FormattedMessage
          id="schedule.missing.metadata"
          defaultMessage="No hay información de programación disponible"
        />
      </span>
    )
  ), [contentImage, isThereTitleOrSchedules]);

  return (
    <div className="dtv-html5-c-mini-epg-detail-card">
      <div
        className={`dtv-html5-c-mini-epg-detail-card__preview ${
          isPlayingNowFlag ? 'dtv-html5-c-mini-epg-detail-card__preview-live' : ''
        }`}
      >
        {blackout ? (
          <span className="dtv-html5-c-mini-epg-detail-card__blackout-text">
            <FormattedMessage
              id="common.weAreSorry"
              defaultMessage="¡Lo sentimos!"
            />
            {' '}
            <FormattedMessage
              id="epg.detail.blackout.subtitle"
              defaultMessage="Debido a derechos de transmisión, este contenido no se encuentra disponible en DIRECTV GO."
            />
          </span>
        ) : (
          <>
            {nowPlayingProgressInfo}
            {imageSchedule}
          </>
        )}
      </div>
    </div>
  );
}

MiniEpgDetailCard.propTypes = {
  focusedSchedule: PropTypes.shape({}),
};

MiniEpgDetailCard.defaultProps = {
  focusedSchedule: {},
};

export default memo(MiniEpgDetailCard);
